// Fetch helpers for the Home Value AVM island (SUR-2152 / SUR-1866).
//
// All calls are same-origin against the Connect subdomain's /_avm and
// registration endpoints. POSTs use form-encoding (Rails reads them as params,
// no JSON content-type / CSRF token needed — the controllers skip CSRF and the
// attribution POST is guarded server-side by Origin matching, which the browser
// sets automatically on a same-origin POST). credentials:"same-origin" so the
// Connect registration session cookie rides along on the gated poll.

const PAGE_TYPE = "home_value";

function form(params) {
  const body = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") body.append(k, v);
  });
  return body;
}

async function getJson(url) {
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  let data = {};
  try {
    data = await res.json();
  } catch (_e) {
    /* non-JSON body — leave data empty */
  }
  return { status: res.status, data };
}

async function postForm(url, params) {
  const res = await fetch(url, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/x-www-form-urlencoded" },
    credentials: "same-origin",
    body: form(params).toString(),
  });
  let data = {};
  try {
    data = await res.json();
  } catch (_e) {
    /* 204 / non-JSON — leave data empty */
  }
  return { status: res.status, ok: res.ok, data };
}

export const avmApi = {
  // GET /_avm/autocomplete?q= → { status:"ok", suggestions:[{c_id,label}] } | 409 needs_setup | 502 upstream down
  async autocomplete(query) {
    const { status, data } = await getJson(`/_avm/autocomplete?q=${encodeURIComponent(query)}`);
    if (status === 409) return { needsSetup: true, suggestions: [], failed: false };
    // 502 = ez upstream failed. Surfaced as `failed` so the widget can say so;
    // reporting it as an empty list made an outage look like "no matches".
    if (status === 502) return { needsSetup: false, suggestions: [], failed: true };
    return { needsSetup: false, suggestions: data.suggestions || [], failed: false };
  },

  // POST /_avm {c_id} → { user_home_id } | 409 needs_setup
  async create(cId) {
    const { status, data } = await postForm("/_avm", { c_id: cId });
    if (status === 409) return { needsSetup: true };
    return { needsSetup: false, userHomeId: data.user_home_id || null };
  },

  // GET /_avm/:id → 202 processing | 200 ready_locked | 200 {valuation,comps} | 409 | 404
  async poll(userHomeId) {
    const { status, data } = await getJson(`/_avm/${encodeURIComponent(userHomeId)}`);
    if (status === 409) return { state: "needs_setup" };
    if (status === 404) return { state: "error" };
    if (status === 202 || data.status === "processing") return { state: "processing" };
    if (data.status === "ready_locked") return { state: "locked" };
    if (data.status === "ready") {
      return {
        state: "ready",
        valuation: data.valuation,
        property: data.property,
        comps: data.comps,
        deeds: data.deeds,
        mortgages: data.mortgages,
        agentEstimate: data.agent_estimate,
      };
    }
    return { state: "error" };
  },

  // POST /_register {email, phone, page_type}. On success the server always
  // acknowledges generically (never enumerates which addresses exist), but a
  // hard failure (429 rate_limited, 422 invalid identifier) means NO code was
  // sent — return that so the gate doesn't ask for a code that isn't coming.
  // On failure the body's `error` carries the field-accurate message (e.g. a
  // bad phone vs a failed SMS send) — pass it up so the gate can show it.
  async requestCode({ email, phone, identifier }) {
    const { ok, status, data } = await postForm("/_register", { email, phone, identifier, page_type: PAGE_TYPE });
    return { ok, status, error: data.error };
  },

  // POST /auth/connect/code {email|phone, code} → ok establishes the session.
  async verifyCode({ email, phone, code }) {
    const { ok, data } = await postForm("/auth/connect/code", { email, phone, code });
    // needs_email_confirmation: a phone sign-in on a number nothing has proved
    // is only half done — the caller has to collect a second, emailed code
    // before anyone is signed in.
    return {
      ok,
      status: data.status,
      needsEmailConfirmation: Boolean(data.needs_email_confirmation),
      sentTo: data.sent_to,
    };
  },

  // GET /account/session → { signed_in }
  async isRegistered() {
    const { data } = await getJson("/account/session");
    return Boolean(data.signed_in);
  },

  // POST /_lead {name, email, phone, page_type, template_key} → lands a lead in the
  // agent's CRM. Used by the needs_setup fallback (AVM unavailable) so the page
  // still captures the visitor — the old templates' {% lead_form %} did this.
  async submitLead({ name, email, phone, templateKey }) {
    const { ok } = await postForm("/_lead", {
      name,
      email,
      phone,
      page_type: PAGE_TYPE,
      template_key: templateKey,
    });
    return { ok };
  },

  // GET /_avm/saved → { home_values:[{id,user_home_id,address,estimate}] }. Empty
  // for an unregistered visitor (the server gates on the registration session).
  async listSaved() {
    const { data } = await getJson("/_avm/saved");
    return data.home_values || [];
  },

  // DELETE /_avm/saved/:id → 204. Removes one of the visitor's saved valuations.
  async deleteSaved(id) {
    const res = await fetch(`/_avm/saved/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    return res.ok;
  },

  // POST /_avm/:id/attribution {valued_address, estimate, page_type, template_key,
  // monthly_updates_consent} → 204. The consent flag is sent only when the form
  // showed the monthly-email line for this valuation.
  async recordAttribution(userHomeId, { valuedAddress, templateKey, estimate, monthlyUpdatesConsent }) {
    await postForm(`/_avm/${encodeURIComponent(userHomeId)}/attribution`, {
      valued_address: valuedAddress,
      estimate: estimate != null ? String(estimate) : "",
      page_type: PAGE_TYPE,
      template_key: templateKey,
      monthly_updates_consent: monthlyUpdatesConsent ? "1" : undefined,
    });
  },
};
